import type { ScaleProjector } from './scaleClientProjection';

type Position = readonly [number, number];

/**
 * Project Polygon / MultiPolygon geometry into one SVG path. `evenodd`
 * filling preserves polygon holes.
 */
export function scaleGeometrySvgPath(
  geometry: unknown,
  projector: ScaleProjector,
): string {
  if (!geometry || typeof geometry !== 'object') return '';
  const record = geometry as {
    type?: unknown;
    coordinates?: unknown;
    geometries?: unknown;
  };
  if (record.type === 'Polygon') {
    return polygonPath(record.coordinates, projector);
  }
  if (record.type === 'MultiPolygon' && Array.isArray(record.coordinates)) {
    return record.coordinates
      .map((polygon) => polygonPath(polygon, projector))
      .filter(Boolean)
      .join(' ');
  }
  if (record.type === 'GeometryCollection' && Array.isArray(record.geometries)) {
    return record.geometries
      .map((child) => scaleGeometrySvgPath(child, projector))
      .filter(Boolean)
      .join(' ');
  }
  return '';
}

function polygonPath(value: unknown, projector: ScaleProjector): string {
  if (!Array.isArray(value)) return '';
  return value
    .map((ring) => ringPath(ring, projector))
    .filter(Boolean)
    .join(' ');
}

function ringPath(value: unknown, projector: ScaleProjector): string {
  if (!Array.isArray(value)) return '';
  const points = value
    .map((position) => projectPosition(position, projector))
    .filter((point): point is { x: number; y: number } => Boolean(point));
  if (points.length < 3) return '';
  return `${points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ')} Z`;
}

function projectPosition(
  value: unknown,
  projector: ScaleProjector,
): { x: number; y: number } | null {
  if (
    !Array.isArray(value)
    || typeof value[0] !== 'number'
    || typeof value[1] !== 'number'
  ) {
    return null;
  }
  const [lon, lat] = value as unknown as Position;
  return projector.lonLatToClient(lon, lat);
}
